import { FC } from 'react'

import { useParams } from 'react-router-dom'

import {
  ResourceCreateView,
  ResourceDetailView,
  ResourceListView,
  useResourceListPaging,
} from 'core/resources'
import { resourceCatalog } from 'core/types/generated/resources'
import {
  useInternalUserCreate,
  useInternalUserGet,
  useInternalUserUpdate,
  useInternalUsersGet,
} from 'core/types/generated/users/hooks'
import {
  usersRequestSchema,
  usersResponseSchema,
  usersUpdateRequestSchema,
} from 'core/utils/generated/formSchemas'

import { asRequestBody, createdId, listRows, record } from './helpers'

const resource = resourceCatalog.users

type ListQuery = Parameters<typeof useInternalUsersGet>[0]['query']

export const UsersList: FC = () => {
  const { companyId } = useParams<{ companyId: string }>()
  const paging = useResourceListPaging<ListQuery>('cursor')
  const result = useInternalUsersGet({ companyId, query: paging.query })

  return (
    <ResourceListView
      {...listRows(result)}
      paging={paging}
      resource={resource}
      schema={usersResponseSchema}
    />
  )
}

export const UserCreate: FC = () => {
  const { companyId } = useParams<{ companyId: string }>()
  const { mutateAsync } = useInternalUserCreate({ companyId })

  return (
    <ResourceCreateView
      onCreate={async (body) =>
        createdId(await mutateAsync(asRequestBody(body)))
      }
      resource={resource}
      schema={usersRequestSchema}
    />
  )
}

export const UserDetail: FC = () => {
  const { companyId, userId } = useParams<{
    companyId: string
    userId: string
  }>()
  const result = useInternalUserGet({ companyId, userId })
  const update = useInternalUserUpdate({ companyId, userId })

  return (
    <ResourceDetailView
      {...record(result)}
      onSave={async (body) => {
        await update.mutateAsync(asRequestBody(body))
      }}
      resource={resource}
      schema={usersResponseSchema}
      updateSchema={usersUpdateRequestSchema}
    />
  )
}
